(() => {
  'use strict';

  const EVENT_PREFIX = 'fitobotanic_';
  const MAX_VALUE_LENGTH = 120;
  const allowedParameters = new Set([
    'product_count',
    'source',
    'status',
    'search_length',
    'results_count',
    'filter_count',
    'item_position',
  ]);

  const analyticsAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_error) {
      return false;
    }
  };

  const cleanParameters = (parameters = {}) => {
    const clean = {};

    Object.entries(parameters).forEach(([key, value]) => {
      if (!allowedParameters.has(key) || value === undefined || value === null || value === '') return;
      if (key === 'item_position' && (!Number.isInteger(value) || value < 1 || value > 5)) return;
      if (!['string', 'number', 'boolean'].includes(typeof value)) return;
      clean[key] = typeof value === 'string' ? value.slice(0, MAX_VALUE_LENGTH) : value;
    });

    return clean;
  };

  const track = (name, parameters = {}) => {
    if (typeof name !== 'string' || !name.startsWith(EVENT_PREFIX)) return;
    if (!analyticsAllowed()) return;

    const detail = { name, parameters: cleanParameters(parameters) };
    document.dispatchEvent(new CustomEvent('fitobotanic:analytics', { detail }));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: detail.name, ...detail.parameters });
  };

  window.FitobotanicAnalytics = Object.freeze({ track, analyticsAllowed });

  const closest = (target, selector) => (target instanceof Element ? target.closest(selector) : null);

  document.addEventListener('click', (event) => {
    const homeProduct = closest(event.target, '[data-fb-home-product]');
    if (homeProduct) {
      track('fitobotanic_product_selected', {
        source: 'home_selection',
        item_position: Number(homeProduct.dataset.itemPosition),
      });
      return;
    }
    if (closest(event.target, '[data-fb-catalogue-link]')) {
      track('fitobotanic_catalogue_opened', { source: 'home_selection' });
      return;
    }

    const saveRoute = closest(event.target, '[data-fb-save-route]');
    if (saveRoute) {
      track('fitobotanic_routine_saved', { source: 'fitobotanic_index' });
      return;
    }

    const objective = closest(event.target, '[data-fb-objective-card]');
    if (objective) {
      track('fitobotanic_objective_selected', {
        objective_key: objective.dataset.fbObjectiveCard,
        source: objective.closest('[data-fb-protocol-objectives]') ? 'protocol_objectives' : 'objective_hub',
      });
      return;
    }

    const protocol = closest(event.target, '[data-fb-protocol-card]');
    if (protocol) {
      track('fitobotanic_protocol_clicked', {
        protocol_handle: protocol.dataset.productHandle,
        source: 'home_protocols',
      });
      return;
    }

    const biaxial = closest(event.target, '[data-fb-biaxial-card]');
    const biaxialProduct = closest(event.target, '[data-fb-biaxial-product]');
    if (biaxialProduct && closest(event.target, 'a')) {
      track('fitobotanic_biaxial_product_clicked', {
        product_handle: biaxialProduct.dataset.productHandle,
        pair_key: biaxial?.dataset.fbSubobjectivePanel,
        source: 'objective_page',
      });
      return;
    }
    const companion = closest(event.target, '[data-fb-companion-product]');
    if (companion) {
      track('fitobotanic_companion_product_clicked', {
        product_handle: companion.dataset.productHandle,
        status: biaxial?.dataset.fbBiaxialStatus,
        source: biaxial ? 'biaxial_combination' : 'protocol_companions',
      });
      return;
    }

    const path = closest(event.target, '[data-fb-path-card]');
    if (path) {
      track('fitobotanic_cta_clicked', { path_key: path.dataset.fbPathCard, source: 'home_decision' });
      return;
    }

    const remove = closest(event.target, '.cart-items__remove');
    if (remove) {
      remove.dataset.fbRemovalPending = 'true';
      return;
    }

    const checkout = closest(event.target, '#checkout');
    if (checkout) track('fitobotanic_checkout_started', { source: 'cart' });
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[role="search"], [action*="/search"]')) {
      const query = form.querySelector('[name="q"]')?.value || '';
      track('fitobotanic_search_submitted', { search_length: query.trim().length, source: 'site_search' });
    }
  }, true);

  document.addEventListener('fitobotanic:objective-selected', (event) => {
    const detail = event.detail || {};
    track('fitobotanic_objective_selected', {
      objective_key: detail.objective,
      source: 'fitobotanic_index',
    });
  });

  document.addEventListener('change', (event) => {
    const facet = closest(event.target, 'facets-form-component');
    if (!facet) return;
    const selected = facet.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').length;
    track('fitobotanic_filter_changed', { filter_count: selected, source: 'catalog' });
  });

  document.addEventListener('cart:update', (event) => {
    const detail = event.detail || {};
    const data = detail.data || {};
    if (data.didError) return;

    if (data.source === 'product-form-component') {
      track('fitobotanic_add_to_cart', {
        variant_id: data.variantId || detail.sourceId,
        source: 'product_form',
      });
      return;
    }

    if (data.source === 'fitobotanic-biaxial') {
      track('fitobotanic_biaxial_added_to_cart', {
        objective_key: data.objectiveKey,
        subobjective_key: data.subobjectiveKey,
        variant_id: data.variantId,
        product_count: 2,
        source: 'objective_page',
      });
      return;
    }

    const pendingRemoval = document.querySelector('.cart-items__remove[data-fb-removal-pending="true"]');
    if (data.source === 'cart-items-component' && pendingRemoval) {
      track('fitobotanic_cart_item_removed', { source: 'cart' });
      pendingRemoval.removeAttribute('data-fb-removal-pending');
    }
  });

  const protocolHandle = document.querySelector('main[data-product-handle]')?.dataset.productHandle;
  if (protocolHandle) {
    if (document.querySelector('[data-fb-protocol-objectives]')) {
      track('fitobotanic_protocol_viewed', { protocol_handle: protocolHandle, source: 'protocol_page' });
    } else {
      track('fitobotanic_product_viewed', { product_handle: protocolHandle, source: 'product_page' });
    }
  }

  const results = document.querySelector('[data-fb-search-results-count]');
  if (results) {
    const resultsCount = Number.parseInt(results.dataset.fbSearchResultsCount || '0', 10);
    const queryLength = new URLSearchParams(window.location.search).get('q')?.trim().length || 0;
    track(resultsCount === 0 ? 'fitobotanic_search_no_results' : 'fitobotanic_search_results_viewed', {
      search_length: queryLength,
      results_count: Number.isNaN(resultsCount) ? 0 : resultsCount,
      source: 'search_results',
    });
  }

  if (document.querySelector('[data-fb-newsletter-success]')) {
    track('fitobotanic_newsletter_submitted', { source: 'home_newsletter' });
  }

  const biaxialCards = document.querySelectorAll('[data-fb-biaxial-card]');
  if ('IntersectionObserver' in window && biaxialCards.length) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        track('fitobotanic_biaxial_viewed', {
          status: entry.target.dataset.fbBiaxialStatus,
          pair_key: entry.target.dataset.fbSubobjectivePanel,
          source: entry.target.closest('[data-fb-objective-experience]') ? 'objective_page' : 'protocol_page',
        });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.5 });
    biaxialCards.forEach((card) => observer.observe(card));
  }
})();
